import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const textareaVariants = cva(
  "ui-flex ui-w-full ui-rounded-[10px] ui-bg-white ui-text-zinc-800 ui-transition-all ui-outline-none ui-resize-none ui-placeholder:text-zinc-400 [&::-webkit-scrollbar]:ui-hidden [-ms-overflow-style:none] [scrollbar-width:none]",
  {
    variants: {
      variant: {
        outline:
          "input-outline hover:input-outline-hover active:input-outline-active focus:input-outline-focus focus:!ui-bg-white \
          data-[disabled=true]:input-outline-disabled data-[disabled=true]:ui-bg-zinc-50 data-[disabled=true]:ui-opacity-50 data-[error=true]:input-outline-error",
        filled:
          "ui-bg-zinc-400/10 hover:input-filled-hover hover:ui-bg-zinc-400/15 active:input-filled-active focus:input-filled-focus \
          data-[disabled=true]:input-outline-disabled data-[disabled=true]:ui-bg-zinc-400/10 data-[disabled=true]:ui-opacity-60 \
          data-[error=true]:input-outline-error data-[focused=true]:ui-bg-white",
        standard:
          "!ui-rounded-none ui-border-0 ui-border-b-1 ui-border-black-10 ui-bg-transparent \
          hover:ui-border-black-20 focus:ui-border-primary-dark focus:ui-border-b-1 \
          focus:ui-shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-primary-dark-color)_20%,transparent)] \
          ui-transition-all ui-duration-200 \
          data-[disabled=true]:ui-border-gray-light data-[disabled=true]:ui-opacity-50 \
          data-[error=true]:ui-border-red-dark data-[error=true]:ui-shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-red-dark-color)_20%,transparent)]",
        ghost:
          "!ui-rounded-none ui-border-0 ui-bg-transparent \
          ui-transition-all ui-duration-200",
      },
      dimension: {
        xs: "ui-text-text-xs-regular ui-rounded-[8px] ui-px-2 ui-min-h-[20px]",
        sm: "ui-text-text-xs-regular ui-rounded-[8px] ui-px-2 ui-py-1.5 ui-min-h-[80px]",
        md: "ui-text-text-sm-regular ui-rounded-[8px] ui-px-2 ui-py-2 ui-min-h-[100px]",
        lg: "ui-text-text-sm-regular ui-rounded-[10px] ui-px-2.5 ui-py-2.5 ui-min-h-[128px]",
      },
    },
    defaultVariants: {
      variant: "outline",
      dimension: "lg",
    },
  }
);

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  VariantProps<typeof textareaVariants> {
  label?: string;
  error?: boolean;
  required?: boolean;
  maxSize?: number;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      variant,
      dimension,
      label,
      required,
      maxSize,
      error,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = React.useState(false);
    const [charCount, setCharCount] = React.useState(
      typeof value === "string" ? value.length : 0
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (maxSize && e.target.value.length > maxSize) return;
      setCharCount(e.target.value.length);
      onChange?.(e);
    };

    return (
      <div className="ui-flex ui-flex-col ui-w-full ui-gap-0.5">
        <div className=" ui-flex ui-justify-between">
          {label && (
            <label className="ui-text-text-xs-medium ui-text-zinc-500">
              {label}
              {required && <span className="ui-text-red-500"> *</span>}
            </label>
          )}
          {maxSize && (
            <div className="ui-flex ui-justify-end ui-text-text-xs-regular ui-text-zinc-500">
              {charCount} / {maxSize}
            </div>
          )}
        </div>

        <textarea
          ref={ref}
          className={cn(textareaVariants({ variant, dimension }), className)}
          rows={1}
          data-disabled={Boolean(props.disabled)}
          data-error={Boolean(error)}
          data-focused={focused}
          value={value}
          onChange={handleChange}
          onFocus={(e) => {
            setFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            props.onBlur?.(e);
          }}
          {...props}
        />
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
